// deps
import { useState, useCallback, useEffect, useContext, useMemo } from "react";

// header state
import { BillToCompanyContext, GlobalHeaderState, useSessionStorage } from "@swiggy-private/checkout-core";
import { getMarketplaceBusinessLine } from "@payment-client/utils/Headers";

// context
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// helper
import { findBillToCompanyPaymentMethod } from "@payment-client/helpers/BillToCompanyHelper";
import { createEmailAutoSuggestHandler } from "@payment-client/helpers/EmailAutoSuggestHelper";
import { format, isEmpty } from "@swiggy-private/js-utils";

// types
import { MARKETPLACE_BUSINESS_LINE, PAYMENT_CODES } from "@swiggy-private/payment-types";
import { B2CPaymentMethodData, SplitPayData } from "@swiggy-private/payment-ui";
import { IExpenseCodeHalfcardProps, SUBMIT_BUTTON_TEXT, B2CHalfCardConfig, ExpenseCodeCacheData } from "@swiggy-private/payment-ui";
import { DEFAULT_PAYMENTS_RESPONSE_KEY } from "@payment-client/constants/StorageKeys";
import { ShowPaymentCacheValue } from "@payment-client/interfaces/ShowPayments";
import { SESSION_STORAGE_KEYS } from "@swiggy-private/core-ui";
import useBillToCompanySplitPay from "./useBillToCompanySplitPay";

export interface IUseExpenseCodeHalfcardReturn {
    expenseCodeProps: IExpenseCodeHalfcardProps;
    showExpenseCodeHalfCard: boolean;
    isBillToCompanySelected: boolean;
    onOpenHalfcard: () => void;
    setOnSubmit: (callback: () => void) => void;
    setIsFullPaymentMode: (isFullPaymentMode: boolean) => void;
    setSplitPaySelectedMethods: (splitPaySelectedMethods: {
        isSplitpaySelected: boolean;
        methodData: SplitPayData | null;
    }) => void;
    b2CHalfCardConfig: B2CHalfCardConfig | null;
}

const useExpenseCodeHalfcard = (): IUseExpenseCodeHalfcardReturn => {
    const headers = GlobalHeaderState.getHeaders();
    const [dataInCache] = useSessionStorage<ShowPaymentCacheValue | null>(DEFAULT_PAYMENTS_RESPONSE_KEY, null);
    const [expenseCodeDataInCache] = useSessionStorage<ExpenseCodeCacheData | null>(
        SESSION_STORAGE_KEYS.EXPENSE_CODE_DATA,
        null,
    );
    const { handleSplitPayMethodSelection } = useBillToCompanySplitPay();
    const { _rawPaymentResponse: contextRawPaymentResponse } = useContext(ShowPaymentContext);
    const [isSelected] = useSessionStorage<boolean>(SESSION_STORAGE_KEYS.BILL_TO_COMPANY_SELECTED, false);
    const { setIsSelected } = useContext(BillToCompanyContext);
    const [splitPaySelectedMethods, setSplitPaySelectedMethods] = useState<{
        isSplitpaySelected: boolean;
        methodData: SplitPayData | null;
    }>({
        isSplitpaySelected: false,
        methodData: null,
    });
    const _rawPaymentResponse = contextRawPaymentResponse || dataInCache?.paymentResponse;
    
    // Called from UI to set the callback
    const [isOpen, setIsOpen] = useState(false);
    const [fieldValue, setFieldValue] = useState({
        projectCode: expenseCodeDataInCache?.projectCode ?? "",
        purposeOfOrdering: expenseCodeDataInCache?.purposeOfOrdering ?? "",
    });
    const [b2cPaymentMethod, setB2cPaymentMethod] = useState<{
        expenseCodeRequired: boolean;
        showExpenseCodeHalfCard: boolean;
        expenseCodes: string[];
        payableAmount: string;
        billUploadRequired: boolean;
        b2CHalfCardConfig: B2CHalfCardConfig | null;
    } | null>(null);
    const [isFullPaymentMode, setIsFullPaymentMode] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-empty-function -- no-op
    const [onSubmitCallback, setOnSubmitCallback] = useState<() => void>(() => {});

    const isMarketplaceBusinessLineRegular = useMemo(() => {
        return getMarketplaceBusinessLine(headers) === MARKETPLACE_BUSINESS_LINE.REGULAR;
    }, [headers]);

    const isMarketplaceBusinessLineDineout = useMemo(() => {
        return getMarketplaceBusinessLine(headers) === MARKETPLACE_BUSINESS_LINE.DINEOUT;
    }, [headers]);

    const onCloseHalfcard = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleInputChange = useCallback((fieldName: string, value: string) => {
        setFieldValue((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    }, []);

    const handleSubmit = useCallback(() => {
        if (onSubmitCallback) {
            onSubmitCallback();
        }
        if (!isEmpty(splitPaySelectedMethods.methodData)) {
            // share splitpay state cart <> payments page if expense code halfcard is enabled and halfcard submitted
            handleSplitPayMethodSelection(
                splitPaySelectedMethods.isSplitpaySelected,
                splitPaySelectedMethods.methodData,
            );
        }
        setIsSelected && setIsSelected(true);
        setIsOpen(false);
    }, [onSubmitCallback, setIsSelected]);

    const setOnSubmit = useCallback((callback: () => void) => {
        setOnSubmitCallback(() => callback);
    }, []);

    const shouldRedirectToPlaceOrder = useMemo(() => {
        // Check if BAU fields have values
        const bauFieldsHaveValues = !isEmpty(fieldValue.projectCode) || !isEmpty(fieldValue.purposeOfOrdering);
        
        // Check if using generic flow with custom fields
        const hasGenericFields = !isEmpty(b2cPaymentMethod?.b2CHalfCardConfig) && 
                                 !isEmpty(b2cPaymentMethod?.b2CHalfCardConfig?.fields);
        
        // For generic flow, assume fields will be filled (redirect should happen)
        // For BAU flow, check if fields actually have values
        const fieldsAreNotEmpty = hasGenericFields || bauFieldsHaveValues;
        
        // here we are setting false if fields are empty in this case halfcard should get closed immediately
        return isMarketplaceBusinessLineRegular && fieldsAreNotEmpty;
    }, [isMarketplaceBusinessLineRegular, fieldValue, b2cPaymentMethod?.b2CHalfCardConfig]);

    const submitButtonText = useMemo(() => {
        const businessLine = getMarketplaceBusinessLine(headers);

        switch (businessLine) {
            case MARKETPLACE_BUSINESS_LINE.REGULAR:
                // turbo payment on payment page
                if (isFullPaymentMode) {
                    return format(SUBMIT_BUTTON_TEXT.REGULAR, b2cPaymentMethod?.payableAmount ?? "");
                }

                // split pay
                return SUBMIT_BUTTON_TEXT.DEFAULT;
            case MARKETPLACE_BUSINESS_LINE.DINEOUT:
                return b2cPaymentMethod?.billUploadRequired ? SUBMIT_BUTTON_TEXT.DINEOUT : SUBMIT_BUTTON_TEXT.DEFAULT;
            default:
                return "";
        }
    }, [headers, b2cPaymentMethod?.payableAmount, isFullPaymentMode, b2cPaymentMethod?.billUploadRequired]);

    const showDescription = useMemo(() => {
        return isMarketplaceBusinessLineDineout && b2cPaymentMethod?.billUploadRequired;
    }, [isMarketplaceBusinessLineDineout, b2cPaymentMethod?.billUploadRequired]);

    const onOpenHalfcard = useCallback(() => {
        setIsOpen(true);
    }, []);

    useEffect(() => {
        if (isEmpty(_rawPaymentResponse)) {
            setB2cPaymentMethod(null);
            return;
        }

        const result = findBillToCompanyPaymentMethod(_rawPaymentResponse, PAYMENT_CODES?.MMT);
        if (result.isSome()) {
            const method = result.unwrap();
            setB2cPaymentMethod({
                expenseCodeRequired: method?.meta?.expenseCodeRequired ?? false,
                showExpenseCodeHalfCard: method?.meta?.showExpenseCodeHalfCard ?? false,
                expenseCodes: method?.meta?.expenseCodes ?? [],
                payableAmount: (method as B2CPaymentMethodData)?.payableAmount || "",
                billUploadRequired: method?.meta?.billUploadRequired ?? false,
                b2CHalfCardConfig: (method as B2CPaymentMethodData)?.meta?.b2CHalfCardConfig ?? null
            });
        } else {
            setB2cPaymentMethod(null);
        }
    }, [_rawPaymentResponse]);

    useEffect(() => {
        if (isOpen) {
            setFieldValue({
                projectCode: expenseCodeDataInCache?.projectCode ?? "",
                purposeOfOrdering: expenseCodeDataInCache?.purposeOfOrdering ?? "",
            });
        }
    }, [isOpen, expenseCodeDataInCache]);

    // api call handlers for email auto-suggest
    const apiCallHandlers = useMemo(() => {
        return createEmailAutoSuggestHandler();
    }, []);

    const expenseCodeProps: IExpenseCodeHalfcardProps = useMemo(() => {
        return {
            isOpen,
            fieldValue,
            projectCodeValues: b2cPaymentMethod?.expenseCodes ?? [],
            requiredFields: {
                projectCode: b2cPaymentMethod?.expenseCodeRequired ?? false,
                purposeOfOrdering: false,
            },
            submitButtonText,
            showDescription,
            expenseCodeDataInCache,
            isRedirectToPlaceOrder: shouldRedirectToPlaceOrder,
            handleInputChange,
            onSubmit: handleSubmit,
            onClose: onCloseHalfcard,
            b2CHalfCardConfig: b2cPaymentMethod?.b2CHalfCardConfig ?? undefined,
            apiCallHandlers,
        };
    }, [
        isOpen,
        fieldValue,
        b2cPaymentMethod,
        submitButtonText,
        shouldRedirectToPlaceOrder,
        expenseCodeDataInCache,
        showDescription,
        handleInputChange,
        handleSubmit,
        onCloseHalfcard,
        apiCallHandlers,
    ]);

    return {
        expenseCodeProps,
        showExpenseCodeHalfCard: b2cPaymentMethod?.showExpenseCodeHalfCard ?? false,
        onOpenHalfcard,
        setOnSubmit,
        isBillToCompanySelected: Boolean(isSelected),
        setIsFullPaymentMode,
        setSplitPaySelectedMethods,
        b2CHalfCardConfig: b2cPaymentMethod?.b2CHalfCardConfig ?? null,
    };
};

export default useExpenseCodeHalfcard;
